// src/features/vita/deeplink/deepLinkRoutes.js
//
// Mapa ÚNICO de deep link → destino dentro do app.
//
// A VITA termina interações mandando o aluno para o app com um link contextual
// ("treino salvo → abre o treino"). Isso serve a dois propósitos ao mesmo tempo:
// concentra o uso no app, onde a leitura é melhor, e reduz o custo de mensagem
// a partir de outubro/2026, quando cada bolha do WhatsApp passa a ser cobrada.
//
// Formato do link, montado pelo backend em app/services/vita_deeplink.py:
//     https://www.lesvita.com/app/<destino>?id=<opcional>&origem=vita
//
// Por que https e não o scheme `lesvita://`: no WhatsApp um scheme desconhecido
// NÃO vira link clicável — o aluno veria texto morto. O https é clicável em
// qualquer lugar, e o App Link (Android) / Universal Link (iOS) decide abrir o
// app quando instalado. Sem app, cai na web autenticada. Um link, dois destinos.
//
// O `lesvita://` continua existindo para o atalho da tela inicial e para
// notificações, onde quem abre é o próprio sistema.

/** Prefixo das rotas de deep link. Fora do curinga `/:referralCode`. */
export const PREFIXO = "/app";

/**
 * Hosts que podem originar um deep link nosso.
 *
 * Precisa ser conferido, e não presumido: `App.getLaunchUrl()` devolve a URL
 * que abriu o app, seja ela qual for. Sem esta allowlist, qualquer
 * `https://<host>/app/treino` seria aceito como link do LesVita — os
 * intent-filters do Android já filtram por host, mas a checagem não pode
 * depender só deles.
 *
 * Espelha os hosts declarados no AndroidManifest e no Associated Domains.
 */
export const HOSTS = Object.freeze(["www.lesvita.com", "lesvita.com"]);

function hostPermitido(hostname) {
  return HOSTS.includes(String(hostname || "").toLowerCase());
}

/**
 * destino → como abrir no app.
 *
 *   aba    : aba de PlataformaPessoal a ativar
 *   query  : parâmetros extras acrescentados à URL da plataforma
 *   evento : CustomEvent disparado depois da navegação (para hosts montados,
 *            como o chat da VITA, que não são rota)
 */
export const DESTINOS = {
  vita: { aba: "vita", query: { vitaChat: "1" } },
  // "Entrar" da landing e o "Fazer login no app" do funil apontam para cá.
  // Não abre tela de login própria: a plataforma já manda para /login quando
  // não há sessão, e quem JÁ está logado cai direto na home — que é o que a
  // pessoa quer nos dois casos.
  login: { aba: "home", query: {} },
  treino: { aba: "treino", query: {}, param: "treinoId" },
  avaliacoes: { aba: "avaliacao", query: {}, param: "analiseId" },
  dieta: { aba: "nutricao", query: {}, param: "dietaId" },
  plano: { aba: "nutricao", query: { plano: "1" } },
  lista: { aba: "nutricao", query: { lista: "1" } },
  agenda: { aba: "treino", query: { agenda: "1" } },
  premium: { aba: "vita", query: { premium: "1" } },
  // Link "seguir" do perfil social: /app/perfil?id=<uid> abre o app direto no
  // perfil daquela pessoa (PerfilDeepLinkBridge lê perfilUid e abre o
  // offcanvas global). Sem app, cai na web autenticada — mesmo destino.
  perfil: { aba: "home", query: {}, param: "perfilUid" },
  // Link de um post: /app/post?id=<postId>. Mesmo trilho do perfil — é o que
  // já tem Universal Link e App Links configurados para /app/*.
  post: { aba: "home", query: {}, param: "postId" },
  // Programa Criadores Parceiros: /app/criador?id=ganhos abre a plataforma do
  // criador (SSO) já na aba pedida. É o destino dos pushes do programa.
  criador: { aba: "perfil", query: {}, param: "criador" },
};

export const ROTA_PLATAFORMA = "/plataforma/pessoal";

/** Evento aberto para o host do chat (mesmo padrão do push). */
export const EVENTO_ABRIR_CHAT = "lesvita:abrir-vita-chat";

/**
 * Extrai (destino, params) de uma URL de deep link.
 * Aceita as duas formas: https://.../app/treino?id=1 e lesvita://treino?id=1.
 * Retorna null quando a URL não é um deep link nosso.
 */
export function parseDeepLink(href) {
  if (!href) return null;
  let url;
  try {
    url = new URL(href);
  } catch {
    return null;
  }

  let destino = null;
  if (url.protocol === "lesvita:") {
    // lesvita://treino?id=1 → host "treino"; lesvita:///treino → pathname
    destino = (url.hostname || url.pathname.replace(/^\/+/, "")).split("/")[0];
  } else if (hostPermitido(url.hostname) && url.pathname.startsWith(`${PREFIXO}/`)) {
    destino = url.pathname.slice(PREFIXO.length + 1).split("/")[0];
  }
  if (!destino) return null;

  destino = destino.toLowerCase();
  if (!DESTINOS[destino]) return null;

  const params = {};
  url.searchParams.forEach((valor, chave) => {
    params[chave] = valor;
  });
  return { destino, params };
}

/**
 * URL interna (rota + query) para onde navegar.
 * Sempre volta para a plataforma: o app é uma tela só com abas, então "abrir o
 * treino" é ativar a aba certa com o id certo — não navegar para outra página.
 */
export function paraRotaInterna(destino, params = {}) {
  const config = DESTINOS[destino];
  if (!config) return ROTA_PLATAFORMA;

  const query = new URLSearchParams({ aba: config.aba, ...config.query });
  if (config.param && params.id) query.set(config.param, params.id);
  // `origem` alimenta a analytics de handoff: quanto do uso do app veio do chat.
  if (params.origem) query.set("origem", params.origem);
  return `${ROTA_PLATAFORMA}?${query.toString()}`;
}

/** Deep link é para o chat da VITA? (host montado, não rota) */
export function abreOChat(destino) {
  return destino === "vita";
}
